import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  ShieldCheck, WarningCircle, ArrowClockwise, Lightning, LockKey,
  KeyReturn, User, Trash, Fingerprint, Clock,
} from "@phosphor-icons/react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";

/**
 * `/admin/security` — real-time cybersecurity operations dashboard.
 *
 * Sólo admin. Consume `GET /api/security/dashboard` (endpoint agregado
 * de `security_router`). Renderiza:
 *  - 5 KPI cards (fallos 24h, éxitos 24h, lockouts 24h, resets 7d, activos).
 *  - Tabla de "Bloqueos activos" con acción `Liberar` (DELETE /security/lockouts).
 *  - Top 10 IPs ofensivos + top 10 cuentas atacadas.
 *  - Timeline de los últimos 50 eventos de seguridad.
 *
 * Se auto-refresca cada 60 s (solo cuando la pestaña está visible).
 * Manual refresh disponible con la tecla `R`.
 */

const EVENT_COLORS = {
  login: "bg-slate-500",
  login_lockout: "bg-red-600",
  login_lockout_email: "bg-red-700",
  lockout_cleared: "bg-emerald-600",
  password_reset: "bg-blue-600",
  password_forgot: "bg-amber-600",
  password_changed: "bg-teal-600",
  user_permissions_updated: "bg-purple-600",
  user_created: "bg-indigo-500",
  user_deleted: "bg-rose-600",
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-PE", {
      day: "2-digit", month: "2-digit", year: "2-digit",
      hour: "2-digit", minute: "2-digit", hour12: false,
    });
  } catch { return iso; }
}

function KpiCard({ label, value, tone = "slate", icon: Icon, hint }) {
  const tones = {
    slate: "bg-slate-900 text-white",
    red: "bg-red-600 text-white",
    green: "bg-emerald-600 text-white",
    amber: "bg-amber-500 text-slate-950",
    blue: "bg-blue-800 text-white",
  };
  return (
    <div className={`rounded-lg p-4 ${tones[tone] || tones.slate}`}
          data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[10px] uppercase tracking-widest opacity-80">{label}</p>
          <p className="font-heading font-black text-3xl mt-1 tabular-nums">{value}</p>
        </div>
        {Icon && <Icon size={26} weight="fill" className="opacity-70" />}
      </div>
      {hint && <p className="text-[11px] mt-2 opacity-70">{hint}</p>}
    </div>
  );
}

export default function SecurityDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [tick, setTick] = useState(0);
  const abortRef = useRef(null);

  // Iter117: intervalo relajado a 60 s (antes 20 s) + AbortController que
  // cancela el fetch anterior antes de disparar uno nuevo. Con esto
  // dejamos de saturar Cloudflare con requests solapados y evitamos las
  // desconexiones abruptas que generaban 520 en producción.
  const load = useCallback(async () => {
    // Cancelar cualquier fetch previo en curso — evita responses solapadas.
    if (abortRef.current) {
      try { abortRef.current.abort(); } catch (_) { /* noop */ }
    }
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    try {
      const r = await api.get("/security/dashboard", { signal: controller.signal });
      if (!controller.signal.aborted) setData(r.data);
    } catch (e) {
      // Silenciar cancelaciones intencionales; el resto sí se muestra.
      if (e?.name === "CanceledError" || e?.code === "ERR_CANCELED" || controller.signal.aborted) return;
      toast.error(apiError(e));
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [tick, load]);

  useEffect(() => {
    // Auto-refresh cada 60 s SOLO cuando la pestaña está visible.
    // Al ocultarse (usuario cambia de tab / bloquea pantalla) pausamos el
    // timer para no acumular requests fantasma que luego generan 520.
    let id = null;
    const start = () => {
      if (id) return;
      id = setInterval(() => setTick((n) => n + 1), 60_000);
    };
    const stop = () => {
      if (id) { clearInterval(id); id = null; }
    };
    const onVis = () => { document.hidden ? stop() : start(); };
    if (!document.hidden) start();
    document.addEventListener("visibilitychange", onVis);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVis);
      if (abortRef.current) { try { abortRef.current.abort(); } catch (_) { /* noop */ } }
    };
  }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "r" && !e.metaKey && !e.ctrlKey && document.activeElement.tagName !== "INPUT") load(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [load]);

  const clearLockout = async (identifier) => {
    if (!window.confirm(`¿Liberar lockout de "${identifier}"? La cuenta podrá volver a intentar iniciar sesión inmediatamente.`)) return;
    try {
      // encodeURIComponent handles colons and @ in the identifier
      await api.delete(`/security/lockouts/${encodeURIComponent(identifier)}`);
      toast.success("Lockout liberado — se registró en el audit log");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const filteredEvents = useMemo(() => {
    if (!data?.recent_events) return [];
    if (!filter.trim()) return data.recent_events;
    const q = filter.toLowerCase().trim();
    return data.recent_events.filter((e) => (
      e.event?.toLowerCase().includes(q)
      || e.actor?.toLowerCase().includes(q)
      || e.ip?.toLowerCase().includes(q)
      || e.detail?.toLowerCase().includes(q)
    ));
  }, [data, filter]);

  if (loading && !data) {
    return <div className="flex h-[60vh] items-center justify-center text-slate-500">Cargando panel de ciberseguridad…</div>;
  }
  const k = data?.kpis || {};
  return (
    <div className="space-y-6" data-testid="security-dashboard">
      <header className="flex flex-wrap items-center gap-3 justify-between">
        <div>
          <h1 className="font-heading font-black text-3xl tracking-tight text-slate-900 flex items-center gap-2">
            <ShieldCheck size={28} weight="fill" className="text-slate-800" />
            Ciberseguridad · Panel de operaciones
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Vigilancia en tiempo real de intentos de login, lockouts activos y eventos críticos.
            <span className="ml-2 text-xs">Actualiza cada 20 s (o pulsa <b>R</b>).</span>
          </p>
        </div>
        <Button variant="outline" onClick={load} data-testid="security-refresh-btn" className="gap-2">
          <ArrowClockwise size={15} /> Refrescar
        </Button>
      </header>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KpiCard label="Fallos 24h" value={k.failed_logins_24h ?? 0} tone="red"
                 icon={WarningCircle} hint="Login inválidos en 24h" />
        <KpiCard label="Éxitos 24h" value={k.successful_logins_24h ?? 0} tone="green"
                 icon={User} hint="Sesiones iniciadas OK" />
        <KpiCard label="Lockouts 24h" value={k.lockouts_triggered_24h ?? 0} tone="amber"
                 icon={LockKey} hint="Cuentas bloqueadas hoy" />
        <KpiCard label="Resets 7d" value={k.password_resets_7d ?? 0} tone="blue"
                 icon={KeyReturn} hint="Autoservicio esta semana" />
        <KpiCard label="Activos" value={k.active_lockouts ?? 0}
                 tone={(k.active_lockouts ?? 0) > 0 ? "red" : "slate"}
                 icon={Lightning} hint="Bloqueos vigentes ahora" />
      </div>

      {/* Active lockouts + top IPs */}
      <div className="grid md:grid-cols-2 gap-4">
        <Card className="p-4">
          <h2 className="font-heading font-black text-lg text-slate-900 flex items-center gap-2">
            <LockKey size={18} weight="fill" className="text-red-600" /> Bloqueos activos
          </h2>
          {(!data?.active_lockouts?.length) ? (
            <p className="text-sm text-slate-400 mt-3">Sin lockouts vigentes.</p>
          ) : (
            <table className="w-full data-table mt-3" data-testid="active-lockouts-table">
              <thead><tr><th>Identificador</th><th>Alcance</th><th className="text-right">Intentos</th><th>Último</th><th></th></tr></thead>
              <tbody>
                {data.active_lockouts.map((lo) => (
                  <tr key={lo.target}>
                    <td className="font-mono text-xs">{lo.target}</td>
                    <td>
                      <Badge className={lo.scope === "email" ? "bg-red-100 text-red-700 border-red-300" : "bg-slate-100 text-slate-700 border-slate-300"}>
                        {lo.scope === "email" ? "email" : "ip+email"}
                      </Badge>
                    </td>
                    <td className="text-right font-mono font-bold text-red-600">{lo.count}/{lo.threshold}</td>
                    <td className="text-xs text-slate-500">{fmtDate(lo.last_attempt)}</td>
                    <td>
                      <Button size="sm" variant="outline" className="gap-1.5"
                        data-testid={`clear-lockout-${lo.target}`}
                        onClick={() => clearLockout(lo.scope === "email" ? `email:${lo.target}` : lo.target)}>
                        <Trash size={12} /> Liberar
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>

        <Card className="p-4">
          <h2 className="font-heading font-black text-lg text-slate-900 flex items-center gap-2">
            <Fingerprint size={18} weight="fill" className="text-slate-800" /> Top ofensores (24h)
          </h2>
          <div className="grid grid-cols-2 gap-4 mt-3">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-slate-400 mb-1.5">IPs sospechosos</p>
              {(!data?.top_offending_ips?.length) ? (
                <p className="text-xs text-slate-400">Sin actividad.</p>
              ) : (
                <ul className="space-y-1" data-testid="top-offending-ips">
                  {data.top_offending_ips.map((r) => (
                    <li key={r.ip} className="flex justify-between text-xs font-mono">
                      <span>{r.ip}</span>
                      <b className="text-red-600">{r.failed_attempts}</b>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest text-slate-400 mb-1.5">Cuentas atacadas</p>
              {(!data?.top_targeted_accounts?.length) ? (
                <p className="text-xs text-slate-400">Sin actividad.</p>
              ) : (
                <ul className="space-y-1" data-testid="top-targeted-accounts">
                  {data.top_targeted_accounts.map((r) => (
                    <li key={r.email} className="flex justify-between text-xs font-mono">
                      <span className="truncate max-w-[140px]" title={r.email}>{r.email}</span>
                      <b className="text-amber-600">{r.failed_attempts}</b>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </Card>
      </div>

      {/* Timeline */}
      <Card className="p-4">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <h2 className="font-heading font-black text-lg text-slate-900 flex items-center gap-2">
            <Clock size={18} weight="fill" className="text-slate-800" /> Timeline · últimos {data?.recent_events?.length ?? 0} eventos
          </h2>
          <Input
            data-testid="security-events-filter"
            className="max-w-xs"
            placeholder="Filtrar por evento, IP, actor…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full data-table" data-testid="security-events-table">
            <thead><tr><th>Fecha</th><th>Evento</th><th>Estado</th><th>Actor</th><th>IP</th><th>Detalle</th></tr></thead>
            <tbody>
              {filteredEvents.length === 0 ? (
                <tr><td colSpan={6} className="text-center text-slate-400 py-6">Sin eventos que coincidan.</td></tr>
              ) : filteredEvents.map((e, i) => (
                <tr key={`${e.created_at}-${i}`}>
                  <td className="text-xs font-mono">{fmtDate(e.created_at)}</td>
                  <td>
                    <span className={`inline-block h-2 w-2 rounded-full mr-2 ${EVENT_COLORS[e.event] || "bg-slate-400"}`} />
                    <span className="text-xs uppercase tracking-wider font-medium">{e.event}</span>
                  </td>
                  <td>
                    {e.success ? (
                      <Badge className="bg-emerald-100 text-emerald-700 border-emerald-300">OK</Badge>
                    ) : (
                      <Badge className="bg-red-100 text-red-700 border-red-300">FAIL</Badge>
                    )}
                  </td>
                  <td className="text-xs">{e.actor || "—"}</td>
                  <td className="text-xs font-mono text-slate-500">{e.ip || "—"}</td>
                  <td className="text-xs text-slate-600 max-w-md truncate" title={e.detail}>{e.detail || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
